import { Pencil, Plus, Star, Trash2 } from "lucide-react";
import {
  MAX_USER_TEMPLATES,
  PROVIDER_LABELS,
  presetRef,
  TEMPLATE_PRESETS,
  type ForwardingSettings,
  type UserTemplate,
} from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { buildPreviewHtml } from "@/lib/forwarding";
import { MessagePreview } from "@/components/forwarding/MessagePreview";
import { TemplateRefSelect } from "@/components/forwarding/TemplateRefSelect";
import { Toggle } from "@/components/forwarding/controls";

const PLATFORM_PROVIDERS = [
  "amazon",
  "mercadolivre",
  "shopee",
  "magalu",
  "aliexpress",
  "shein",
  "awin",
];

type Preset = (typeof TEMPLATE_PRESETS)[number];

function DefaultBadge(): JSX.Element {
  return (
    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
      Padrão
    </span>
  );
}

function PresetCard({
  preset,
  settings,
  onUseAsBase,
  onSetDefault,
}: {
  preset: Preset;
  settings: ForwardingSettings;
  onUseAsBase: (name: string, body: string) => void;
  onSetDefault: (ref: string) => void;
}): JSX.Element {
  const ref = presetRef(preset.key);
  const isDefault = (settings.defaultTemplateRef ?? presetRef("padrao")) === ref;
  return (
    <li className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-foreground">{preset.name}</p>
          <p className="text-xs text-muted-foreground">{preset.description}</p>
        </div>
        {isDefault && <DefaultBadge />}
      </div>
      <MessagePreview
        html={buildPreviewHtml(settings, [], "shopee", preset.body)}
        platform="telegram"
      />
      <div className="mt-auto flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onUseAsBase(`${preset.name} (meu)`, preset.body)}
        >
          <Pencil size={13} /> Usar como base
        </Button>
        <Button size="sm" variant="ghost" disabled={isDefault} onClick={() => onSetDefault(ref)}>
          <Star size={13} /> Definir como padrão
        </Button>
      </div>
    </li>
  );
}

export function Gallery({
  settings,
  onUseAsBase,
  onSetDefault,
}: {
  settings: ForwardingSettings;
  onUseAsBase: (name: string, body: string) => void;
  onSetDefault: (ref: string) => void;
}): JSX.Element {
  return (
    <ul className="grid gap-3 md:grid-cols-2">
      {TEMPLATE_PRESETS.map((preset) => (
        <PresetCard
          key={preset.key}
          preset={preset}
          settings={settings}
          onUseAsBase={onUseAsBase}
          onSetDefault={onSetDefault}
        />
      ))}
    </ul>
  );
}

interface TemplateActions {
  readonly onEdit: (t: UserTemplate) => void;
  readonly onDelete: (id: string) => void;
  readonly onSetDefault: (ref: string) => void;
}

function TemplateRow({
  template: t,
  isDefault,
  onEdit,
  onDelete,
  onSetDefault,
}: TemplateActions & { template: UserTemplate; isDefault: boolean }): JSX.Element {
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-foreground">{t.name}</p>
        <p className="truncate font-mono text-[11px] text-muted-foreground">
          {t.body.split("\n")[0]}
        </p>
      </div>
      {isDefault ? (
        <DefaultBadge />
      ) : (
        <Button size="sm" variant="ghost" onClick={() => onSetDefault(t.id)}>
          <Star size={13} /> Padrão
        </Button>
      )}
      <Button
        size="icon-sm"
        variant="ghost"
        aria-label={`Editar ${t.name}`}
        onClick={() => onEdit(t)}
      >
        <Pencil size={13} />
      </Button>
      <Button
        size="icon-sm"
        variant="ghost"
        aria-label={`Apagar ${t.name}`}
        onClick={() => onDelete(t.id)}
      >
        <Trash2 size={13} />
      </Button>
    </li>
  );
}

export function MyTemplates({
  templates,
  onNew,
  defaultRef,
  ...actions
}: TemplateActions & {
  templates: readonly UserTemplate[];
  onNew: () => void;
  defaultRef: string | null;
}): JSX.Element {
  const full = templates.length >= MAX_USER_TEMPLATES;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs tabular-nums text-muted-foreground">
          {templates.length}/{MAX_USER_TEMPLATES} templates
        </span>
        <Button size="sm" onClick={onNew} disabled={full}>
          <Plus size={14} /> Novo template
        </Button>
      </div>
      {templates.length === 0 && (
        <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Você ainda não criou templates. Comece do zero ou use um modelo pronto como base.
        </p>
      )}
      <ul className="space-y-2">
        {templates.map((t) => (
          <TemplateRow key={t.id} template={t} isDefault={defaultRef === t.id} {...actions} />
        ))}
      </ul>
    </div>
  );
}

function RefField({
  id,
  label,
  value,
  templates,
  emptyLabel,
  onChange,
}: {
  id: string;
  label: string;
  value: string | null;
  templates: readonly UserTemplate[];
  emptyLabel: string;
  onChange: (ref: string | null) => void;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-xs font-medium text-foreground">
        {label}
      </label>
      <TemplateRefSelect
        id={id}
        value={value}
        templates={templates}
        emptyLabel={emptyLabel}
        onChange={onChange}
      />
    </div>
  );
}

export function PlatformDefaults({
  settings,
  templates,
  onChange,
}: {
  settings: ForwardingSettings;
  templates: readonly UserTemplate[];
  onChange: (next: ForwardingSettings) => void;
}): JSX.Element {
  const setFor = (code: string, ref: string | null): void => {
    const rest = Object.fromEntries(
      Object.entries(settings.templateByProvider).filter(([key]) => key !== code),
    );
    onChange({ ...settings, templateByProvider: ref ? { ...rest, [code]: ref } : rest });
  };
  return (
    <div className="space-y-4">
      <Toggle
        id="tpl-use"
        checked={settings.useTemplate}
        onChange={(useTemplate) => onChange({ ...settings, useTemplate })}
        label="Aplicar template no encaminhamento"
        hint="Desligado, as ofertas encaminhadas mantêm o texto original e só o link muda."
      />
      <RefField
        id="tpl-global"
        label="Padrão global"
        value={settings.defaultTemplateRef}
        templates={templates}
        emptyLabel="Padrão do LucraLink"
        onChange={(defaultTemplateRef) => onChange({ ...settings, defaultTemplateRef })}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        {PLATFORM_PROVIDERS.map((code) => (
          <RefField
            key={code}
            id={`tpl-${code}`}
            label={PROVIDER_LABELS[code] ?? code}
            value={settings.templateByProvider[code] ?? null}
            templates={templates}
            emptyLabel="Usar o padrão global"
            onChange={(ref) => setFor(code, ref)}
          />
        ))}
      </div>
    </div>
  );
}
